// AI-CONTEXT: See docs/patterns.md#hook-permission-gate
// AI-CONTEXT: See docs/features/role-based-permissions.md

import { createContext, useCallback, useContext, useMemo } from "react"
import { Auth } from "./Auth"
import { useFirestoreLoaded, useFirestoreUser } from "../firebase/useFirestore"
import { client } from "../hooks/Client"
import roleWorksIn from "../helpers/roleAreas"

const defaultValue = {
    has: () => false,
    // Onbekende rol toont alles: dit verbergt alleen en beschermt niets, en een
    // scherm dat wegvalt omdat de rol nog niet geladen is, is erger dan een
    // scherm te veel. Zie helpers/roleAreas.js.
    worksIn: () => true,
    myRole: null,
    ready: false,
    isSuperAdmin: false,
    userRoles: [],
}

export const Permissions = createContext(defaultValue)

// Provider runs the UserRoles + current-user listeners ONCE for the whole
// app — without it, every consumer of usePermissions opens its own pair
// of listeners and components on the Settings page would multiply that.
//
// `authO` from Auth context is a one-shot .get() snapshot taken at login.
// useFirestoreUser overlays a live listener so a user who just assigned
// themselves a role sees the change without a page reload.
export const PermissionsProvider = ({ children }) => {
    const [authO] = useContext(Auth)
    const [userRoles, userRolesLoaded] = useFirestoreLoaded("UserRoles")
    const liveUsers = useFirestoreUser(authO?.ID)

    const liveMe = Array.isArray(liveUsers) && liveUsers.length > 0 ? liveUsers[0] : null
    const sourceUser = liveMe || authO

    // Ready once the roles snapshot has returned — even when the tenant has zero
    // roles. A tenant with no roles must resolve to "no access" (handled by the
    // gate via has() → false), not hang forever on a blank screen.
    const ready = !!sourceUser && userRolesLoaded

    // Cross-tenant superadmin: holds every permission in every tenant —
    // present and future — without needing a role assigned. Set by hand on
    // the User document (SuperAdmin: true).
    const isSuperAdmin = sourceUser?.SuperAdmin === true

    const has = useCallback((key) => {
        if (!ready) return false
        if (isSuperAdmin) return true
        const roleId = sourceUser?.UserRoleIDs?.[client]
        if (!roleId) return false
        const role = userRoles.find(r => r.ID === roleId)
        if (!role || !Array.isArray(role.Permissions)) return false
        return role.Permissions.includes(key)
    }, [ready, isSuperAdmin, sourceUser, userRoles])

    // De rol van de ingelogde gebruiker; hier al opgezocht voor `has`, dus dit
    // kost geen extra listener.
    const myRole = useMemo(() => {
        const roleId = sourceUser?.UserRoleIDs?.[client]
        if (!roleId || !Array.isArray(userRoles)) return null
        return userRoles.find((r) => r.ID === roleId) || null
    }, [sourceUser, userRoles])

    /**
     * Of de rol van deze gebruiker in dit onderdeel werkt — puur om menu-items
     * en tabbladen rustiger te maken. Zie helpers/roleAreas.js: dit is een
     * gordijn, de rechten via `has` zijn het slot.
     *
     * Zolang de rollen nog laden toont hij alles: een menu dat halverwege het
     * laden van items verspringt is verwarrender dan een menu dat compleet
     * begint. En een superadmin werkt overal in.
     */
    const worksIn = useCallback((areaKey) => {
        if (!ready || isSuperAdmin) return true
        return roleWorksIn(myRole, areaKey)
    }, [ready, isSuperAdmin, myRole])

    const value = useMemo(() => ({
        has,
        worksIn,
        myRole,
        ready,
        isSuperAdmin,
        userRoles: Array.isArray(userRoles) ? userRoles : [],
    }), [has, worksIn, myRole, ready, isSuperAdmin, userRoles])

    return (
        <Permissions.Provider value={value}>
            {children}
        </Permissions.Provider>
    )
}
